import React, { useState } from "react";

function ToggleList() {
  // 씬(장면) 목록 초기값
  const [scenes, setScenes] = useState([
    {
      id: 1,
      title: "Opening Scene",
      duration: "0-4 sec",
      required: true,
      image: null, // 업로드된 File 객체
      imagePreview: "", // 미리보기 URL
      text: "",
    },
    {
      id: 2,
      title: "Property Location",
      duration: "0-5 sec",
      required: true,
      image: null,
      imagePreview: "",
      text: "",
    },
    {
      id: 3,
      title: "Property Details",
      duration: "0-4 sec",
      required: true,
      image: null,
      imagePreview: "",
      text: "",
    },
    {
      id: 4,
      title: "Realty Information",
      duration: "0-4 sec",
      required: true,
      image: null,
      imagePreview: "",
      text: "",
    },
  ]);

  // 현재 펼쳐져 있는 아코디언(씬) ID
  // null이면 모두 접혀 있음
  const [expandedSceneId, setExpandedSceneId] = useState(1);

  // 아코디언 토글 핸들러
  const toggleScene = (id) => {
    setExpandedSceneId((prev) => (prev === id ? null : id));
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = (sceneId, file) => {
    if (!file) {
      // 파일 제거 시
      setScenes((prevScenes) =>
        prevScenes.map((scene) =>
          scene.id === sceneId
            ? { ...scene, image: null, imagePreview: "" }
            : scene
        )
      );
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setScenes((prevScenes) =>
      prevScenes.map((scene) =>
        scene.id === sceneId
          ? { ...scene, image: file, imagePreview: previewUrl }
          : scene
      )
    );
  };

  // 텍스트 변경 핸들러
  const handleTextChange = (sceneId, newText) => {
    setScenes((prevScenes) =>
      prevScenes.map((scene) =>
        scene.id === sceneId ? { ...scene, text: newText } : scene
      )
    );
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-4">
      {scenes.map((scene, index) => {
        const isOpen = expandedSceneId === scene.id;

        return (
          <div
            key={scene.id}
            className={`border rounded-xl ${
              isOpen ? "ring-2 ring-blue-300" : "shadow-sm"
            } transition-all`}
          >
            {/* 아코디언 헤더 */}
            <div
              onClick={() => toggleScene(scene.id)}
              className={`p-4 flex items-center justify-between cursor-pointer ${
                isOpen ? "bg-blue-50" : "bg-white hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 font-medium">
                  {index + 1}
                </div>
                <div>
                  <h2 className="font-semibold text-gray-800">{scene.title}</h2>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    {scene.duration}
                    {scene.required && (
                      <span className="text-red-500 ml-2">(Required)</span>
                    )}
                  </p>
                </div>
              </div>
              {/* 토글 아이콘 (간단한 화살표) */}
              <div className="text-gray-400">
                {isOpen ? (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                )}
              </div>
            </div>

            {/* 아코디언 바디 (펼쳐져 있을 때만) */}
            {isOpen && (
              <div className="p-4 border-t bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* 왼쪽 컬럼: 이미지 업로드 섹션 */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Scene Image
                    </h4>
                    {scene.imagePreview ? (
                      <div className="relative group w-full h-60 bg-gray-100 rounded-lg overflow-hidden border">
                        <img
                          src={scene.imagePreview}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        {/* 마우스 호버 시 이미지 제거 버튼 */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-colors">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageUpload(scene.id, null);
                            }}
                            className="px-3 py-1 bg-white text-red-500 text-sm font-medium rounded-lg hidden group-hover:block"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label
                        htmlFor={`sceneImage-${scene.id}`}
                        className="flex flex-col items-center justify-center w-full h-60 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <svg
                          className="w-10 h-10 mb-2 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 15a4 4 0 001 7.873m12-7.873h4a2 2 0 012 2v6m-6-6V9a1 1 0 00-1-1H4a1 1 0 00-1 1v6m14 0l-4 4m0-4l-4 4"
                          />
                        </svg>
                        <p className="text-sm text-gray-500">
                          Click to upload or drag & drop
                        </p>
                        <p className="text-xs text-gray-400">
                          JPEG, PNG, WEBP · Max 5MB
                        </p>
                        <input
                          id={`sceneImage-${scene.id}`}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleImageUpload(scene.id, e.target.files[0]);
                            }
                          }}
                        />
                      </label>
                    )}

                    {/* Scene Tips */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-md border text-sm text-gray-600">
                      <h5 className="font-medium mb-2">Scene Tips</h5>
                      <ul className="space-y-1 list-disc ml-5">
                        <li>
                          Use high-resolution photos for best video quality
                        </li>
                        <li>
                          Landscape orientation works best for most scenes
                        </li>
                        <li>
                          Ensure good lighting and clear visibility of features
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* 오른쪽 컬럼: 텍스트 작성 섹션 */}
                  <div className="flex flex-col">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Scene Text
                    </h4>
                    <textarea
                      value={scene.text}
                      onChange={(e) =>
                        handleTextChange(scene.id, e.target.value)
                      }
                      className="w-full h-60 border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Enter text for this scene..."
                    />
                    <div className="mt-1 text-right text-xs text-gray-400">
                      {scene.text.length} characters
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ToggleList;
